import React, { useState, useEffect } from 'react';
import { referralsAPI } from '@/services/api';
import { motion } from 'framer-motion';
import { 
  Users,
  Copy,
  Check,
  Link2,
  DollarSign,
  Gift,
  Share2,
  Clock
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { format } from 'date-fns';

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.3 }
};

export default function ReferralsPage() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await referralsAPI.getStats();
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch referral stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const copyCode = async () => {
    if (stats?.referral_code) {
      await navigator.clipboard.writeText(stats.referral_code);
      setCopied(true);
      toast.success('Referral code copied!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const copyLink = async () => {
    if (stats?.referral_link) {
      await navigator.clipboard.writeText(stats.referral_link);
      setCopiedLink(true);
      toast.success('Referral link copied!');
      setTimeout(() => setCopiedLink(false), 2000);
    }
  };

  const shareLink = async () => {
    if (navigator.share && stats?.referral_link) {
      try {
        await navigator.share({
          title: 'Join Dexfolio',
          text: 'Start earning daily crypto returns with Dexfolio! Use my referral link to join:',
          url: stats.referral_link
        });
      } catch (err) {
        // User cancelled share
      }
    } else {
      copyLink();
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <motion.div 
      initial="initial"
      animate="animate"
      className="space-y-4 sm:space-y-6"
      data-testid="referrals-page"
    >
      {/* Header */}
      <motion.div variants={fadeInUp}>
        <h1 className="text-2xl sm:text-3xl font-outfit font-bold text-white">Referral Program</h1>
        <p className="text-white/60 mt-1 text-sm sm:text-base">Invite friends and earn {stats?.bonus_rate || 5}% of their first investment</p>
      </motion.div>

      {/* Referral Code Card */}
      <motion.div variants={fadeInUp} className="glass-card p-4 sm:p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500/20 to-emerald-500/20 flex items-center justify-center">
            <Gift className="w-6 h-6 text-cyan-400" />
          </div>
          <div>
            <h2 className="text-lg font-outfit font-semibold text-white">Your Referral Code</h2>
            <p className="text-white/50 text-sm">Share this code with friends</p>
          </div>
        </div>
        
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1 flex items-center gap-2">
            <Input
              value={stats?.referral_code || ''}
              readOnly
              className="input-field font-mono text-lg tracking-wider text-center text-cyan-400"
              data-testid="referral-code-input"
            />
            <Button
              variant="ghost"
              size="icon"
              onClick={copyCode}
              className="shrink-0 h-12 w-12 border border-white/10"
              data-testid="copy-code-btn"
            >
              {copied ? <Check className="w-5 h-5 text-emerald-400" /> : <Copy className="w-5 h-5" />}
            </Button>
          </div>
          <Button 
            onClick={shareLink}
            className="btn-primary sm:w-auto"
            data-testid="share-btn"
          >
            <Share2 className="w-4 h-4 mr-2" />
            Share
          </Button>
        </div>

        <div className="mt-4 pt-4 border-t border-white/10">
          <p className="text-white/50 text-sm mb-2">Or share your referral link:</p>
          <div className="flex items-center gap-2">
            <div className="flex-1 p-3 bg-white/5 rounded-lg overflow-hidden">
              <code className="text-xs sm:text-sm text-cyan-400 break-all">
                {stats?.referral_link || ''}
              </code>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={copyLink}
              className="shrink-0 h-10 w-10"
              data-testid="copy-link-btn"
            >
              {copiedLink ? <Check className="w-4 h-4 text-emerald-400" /> : <Link2 className="w-4 h-4" />}
            </Button>
          </div>
        </div>
      </motion.div>

      {/* Stats Cards */}
      <motion.div variants={fadeInUp} className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
        <div className="glass-card p-4 text-center">
          <Users className="w-6 h-6 text-cyan-400 mx-auto mb-2" />
          <p className="text-2xl sm:text-3xl font-mono font-bold text-white">{stats?.total_referrals || 0}</p>
          <p className="text-white/50 text-xs sm:text-sm">Total Referrals</p>
        </div>
        <div className="glass-card p-4 text-center">
          <DollarSign className="w-6 h-6 text-emerald-400 mx-auto mb-2" />
          <p className="text-2xl sm:text-3xl font-mono font-bold text-emerald-400">
            ${(stats?.total_earnings || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
          </p>
          <p className="text-white/50 text-xs sm:text-sm">Total Earnings</p>
        </div>
        <div className="glass-card p-4 text-center">
          <Gift className="w-6 h-6 text-yellow-400 mx-auto mb-2" />
          <p className="text-2xl sm:text-3xl font-mono font-bold text-yellow-400">{stats?.bonus_rate || 5}%</p>
          <p className="text-white/50 text-xs sm:text-sm">Bonus Rate</p>
        </div>
        <div className="glass-card p-4 text-center">
          <Users className="w-6 h-6 text-purple-400 mx-auto mb-2" />
          <p className="text-2xl sm:text-3xl font-mono font-bold text-purple-400">
            {stats?.referred_users?.filter(u => u.investment_amount > 0).length || 0}
          </p>
          <p className="text-white/50 text-xs sm:text-sm">Active Traders</p>
        </div>
      </motion.div>

      {/* How It Works */}
      <motion.div variants={fadeInUp} className="glass-card p-4 sm:p-6">
        <h3 className="text-lg font-outfit font-semibold text-white mb-4">How It Works</h3>
        <div className="grid sm:grid-cols-3 gap-4">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-cyan-500/20 flex items-center justify-center shrink-0 text-cyan-400 font-bold">
              1
            </div>
            <div>
              <p className="text-white font-medium text-sm">Share Your Code</p>
              <p className="text-white/50 text-xs mt-1">Share your unique referral code or link with friends</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-cyan-500/20 flex items-center justify-center shrink-0 text-cyan-400 font-bold">
              2
            </div>
            <div>
              <p className="text-white font-medium text-sm">Friend Signs Up</p>
              <p className="text-white/50 text-xs mt-1">Your friend registers using your referral code</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center shrink-0 text-emerald-400 font-bold">
              3
            </div>
            <div>
              <p className="text-white font-medium text-sm">Earn Bonus</p>
              <p className="text-white/50 text-xs mt-1">Get {stats?.bonus_rate || 5}% of their first investment as bonus</p>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Referred Users */}
      <motion.div variants={fadeInUp} className="glass-card p-4 sm:p-6">
        <h3 className="text-lg font-outfit font-semibold text-white mb-4">Your Referrals</h3>
        
        {stats?.referred_users?.length > 0 ? (
          <div className="space-y-3">
            {stats.referred_users.map((user, index) => (
              <div 
                key={index}
                className="flex items-center justify-between p-3 rounded-lg bg-white/5 border border-white/10"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-cyan-500/20 flex items-center justify-center">
                    <Users className="w-5 h-5 text-cyan-400" />
                  </div>
                  <div>
                    <p className="text-white font-medium text-sm">{user.name}</p>
                    <p className="text-white/50 text-xs flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      Joined {format(new Date(user.joined_at), 'MMM dd, yyyy')}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  {user.bonus_earned > 0 ? (
                    <>
                      <p className="text-emerald-400 font-mono font-bold text-sm">
                        +${user.bonus_earned.toFixed(2)}
                      </p>
                      <p className="text-white/50 text-xs">earned</p>
                    </>
                  ) : (
                    <span className="text-xs px-2 py-1 rounded-full bg-yellow-400/20 text-yellow-400">
                      Pending investment
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <Users className="w-12 h-12 text-white/20 mx-auto mb-4" />
            <p className="text-white/40 mb-4">No referrals yet</p>
            <p className="text-white/50 text-sm">Share your referral code to start earning!</p>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
}
